import * as numeral from 'numeral';
import * as React from 'react';
import {
    EPage,
    ESourceType,
    IAppState,
    Selection,
} from '../Types/Model';
import Context from './Context';

/*interface IWindowWithDataFileUrl extends Window {
    mlrDataFileUrl?: string
}*/

export default class Provider extends React.Component {
    public state = {
        maxPages: 5,
        page: 1,
        userState: {},
    } as IAppState

    public async componentDidMount() {
        const appRoot: HTMLElement | null = document.getElementById('mlr-water-calculator');
        const dataFileUrl: string | null = appRoot && appRoot.getAttribute('data-mlr-data-file-url');

        if (!dataFileUrl) {
            throw new Error('No data file url specified. The url should be defined on window.mlrDataFileUrl');
        }

        // 'https://s3-ap-southeast-2.amazonaws.com/enabled-mlr/data.json'
        const response = await fetch(dataFileUrl);
        const results = await response.json();

        const allowedSpecificationKeys: Array<keyof IAppState> = [
            'activities',
            'crops',
            'fireFighting',
            'garden',
            'gardenMax',
            'gardenStep',
            'horseFloatWashdownLarge',
            'horseFloatWashdownSmall',
            'horseWashdown',
            'household',
            'messages',
            'stock',
            'suburbs',
        ];

        const newState: Partial<IAppState> = { };

        allowedSpecificationKeys.forEach(key => {
            if (results.hasOwnProperty(key)) {
                newState[key] = results[key];
            }
        });

        // Load Spec
        this.setState({
            ...newState,
            userState: {
                ...this.state.userState,
            },
        });

        // Load Default User State
        setTimeout(() => {
            this.setState({
                loaded: true,
                userState: {
                    ...this.state.userState,
                    ...this.getDefaultUserState(),
                },
            });

            // Load User State from localStorage
            this.loadUserState();
        }, 0);
    }

    public render() {
        const data = {
            ...this.state,
            getTotal: this.getTotal,
            isCurrentPageComplete: this.isCurrentPageComplete,
            onAddActivity: (text: string) => this.onAddActivity(text),
            onAddCrop: (text: string) => this.onAddCrop(text),
            onAddOtherActivity: () => this.onAddOtherActivity(),
            onAddOtherCrop: () => this.onAddOtherCrop(),
            onAddOtherUnlicensed: () => this.onAddOtherUnlicensed(),
            onAddStock: (e: React.FormEvent<HTMLSelectElement>) => this.onAddStock(e.currentTarget.value),
            onChangeActivityName: (index: number, text: string) => this.onChangeActivityName(index, text),
            onChangeActivityValue: (index: number, value: string|number) => this.onChangeActivityValue(index, value),
            onChangeClimateZone: (e: React.FormEvent<HTMLSelectElement>) => this.setProperty(ESourceType.ClimateZone, e.currentTarget.value),
            onChangeCropName: (index: number, text: string) => this.onChangeCropName(index, text),
            onChangeCropValue: (index: number, value: string|number) => this.onChangeCropValue(index, value),
            onChangeFireFightingFirePumpAndHosesValue: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.FireFightingFirePumpAndHoses, e.currentTarget.value),
            onChangeFireFightingSprinklerSystemValue: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.FireFightingSprinklerSystem, e.currentTarget.value),
            onChangeGarden: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.Garden, e.currentTarget.value),
            onChangeHorseFloatWashdownLarge: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.HorseFloatWashdownLarge, e.currentTarget.value),
            onChangeHorseFloatWashdownSmall: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.HorseFloatWashdownSmall, e.currentTarget.value),
            onChangeHorseWashdown: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.HorseWashdown, e.currentTarget.value),
            onChangeHousehold: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.Household, e.currentTarget.value),
            onChangeOtherActivityName: (index: number, value: string) => this.onChangeOtherActivityName(index, value),
            onChangeOtherActivityValue: (index: number, value: string|number) => this.onChangeOtherActivityValue(index, value),
            onChangeOtherCropName: (index: number, value: string) => this.onChangeOtherCropName(index, value),
            onChangeOtherCropValue: (index: number, value: string|number) => this.onChangeOtherCropValue(index, value),
            onChangeOtherUnlicensedName: (index: number, value: string) => this.onChangeOtherUnlicensedName(index, value),
            onChangeOtherUnlicensedValue: (index: number, value: string|number) => this.onChangeOtherUnlicensedValue(index, value),
            onChangeStockName: (index: number, value: string) => this.onChangeStockName(index, value),
            onChangeStockValue: (index: number, value: string|number) => this.onChangeStockValue(index, value),
            onChangeSuburb: (e: React.FormEvent<HTMLSelectElement>) => this.setProperty(ESourceType.Suburb, e.currentTarget.value),
            onChangeWeedControl: (e: React.FormEvent<HTMLInputElement>) => this.setProperty(ESourceType.WeedControl, e.currentTarget.value),
            onGotoNamedPage: this.onGotoNamedPage,
            onGotoPage: this.onGotoPage,
            onNextPage: this.onNextPage,
            onPreviousPage: this.onPreviousPage,
            onReset: this.onReset,
            onToggleOption: (key: ESourceType, enabled: boolean) => this.toggleOption(key, enabled),
        };

        return (
            <Context.Provider value={data}>
                {this.props.children}
            </Context.Provider>
        );
    }

    private isCurrentPageComplete = () => {
        const { page, userState } = this.state;

        if (page === 1 && typeof userState.suburb.value === 'string' && typeof userState.climateZone.value === 'string') {
            return userState.suburb.value.trim().length > 0 && userState.climateZone.value.trim().length > 0;
        }

        return true;
    };

    private storeUserState = () => {
        setTimeout(() => {
            const serialized = JSON.stringify(this.state.userState);
            localStorage.setItem('mlr-user-state', serialized);
        }, 0);
    }

    private loadUserState = () => {
        setTimeout(() => {
            const result = localStorage.getItem('mlr-user-state');

            if (result) {
                const deserialized = JSON.parse(result);
                this.setState({
                    userState: deserialized,
                });
            }
        }, 0);
    }

    private onAddStock = (value: string) => {
        const { stockSelections } = this.state.userState;
        const newItem = Selection.newNumber({ name: value, type: ESourceType.Stock });
        stockSelections.push(newItem);

        this.setState({
            userState: {
                ...this.state.userState,
                stockSelections,
            }
        });

        this.updateTotals();
    };

    private onAddOtherUnlicensed = () => {
        const { otherUnlicensedSelections } = this.state.userState;
        const newItem = Selection.newNumber({ type: ESourceType.OtherUnlicensed });
        otherUnlicensedSelections.push(newItem);

        this.setState({
            userState: {
                ...this.state.userState,
                otherUnlicensedSelections,
            }
        });
    };

    private onAddActivity = (text: string) => {
        const { activitySelections } = this.state.userState;
        const foundDatasource = this.state.activities.find(item => item.name === text);
        const tooltip = foundDatasource && foundDatasource.tooltip;
        const newItem = Selection.newNumber({ name: text, tooltip, type: ESourceType.Activity });
        activitySelections.push(newItem);

        this.setState({
            userState: {
                ...this.state.userState,
                activitySelections,
            },
        });
    }

    private onAddCrop = (text: string) => {
        const { cropSelections } = this.state.userState;
        const newItem = Selection.newNumber({ name: text, type: ESourceType.Crop });
        cropSelections.push(newItem);

        this.setState({
            userState: {
                ...this.state.userState,
                cropSelections,
            },
        });
    }

    private onAddOtherActivity = () => {
        const { otherActivitySelections } = this.state.userState;
        const newItem = Selection.newNumber({ type: ESourceType.OtherActivity });
        otherActivitySelections.push(newItem);

        this.setState({
            userState: {
                ...this.state.userState,
                otherActivitySelections,
            },
        });
    };

    private onAddOtherCrop = () => {
        const { otherCropSelections } = this.state.userState;
        const newItem = Selection.newNumber({ type: ESourceType.OtherCrop });
        otherCropSelections.push(newItem);

        this.setState({
            userState: {
                ...this.state.userState,
                otherCropSelections,
            },
        });
    };

    private onChangeOtherActivityName = (index: number, text: string) => {
        const { otherActivitySelections } = this.state.userState;

        if (index >= 0 && index < otherActivitySelections.length) {
            otherActivitySelections[index].name = text;

            this.setState({
                userState: {
                    ...this.state.userState,
                    otherActivitySelections,
                }
            });

            this.updateTotals();
        }
    }
    private onChangeOtherCropName = (index: number, text: string) => {
        const { otherCropSelections } = this.state.userState;

        if (index >= 0 && index < otherCropSelections.length) {
            otherCropSelections[index].name = text;

            this.setState({
                userState: {
                    ...this.state.userState,
                    otherCropSelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeOtherActivityValue = (index: number, value: string|number) => {
        const { otherActivitySelections } = this.state.userState;

        if (index >= 0 && index < otherActivitySelections.length) {
            otherActivitySelections[index].value = value;
            otherActivitySelections[index].total = `${value} kL`;

            this.setState({
                userState: {
                    ...this.state.userState,
                    otherActivitySelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeOtherCropValue = (index: number, value: string|number) => {
        const { otherCropSelections } = this.state.userState;

        if (index >= 0 && index < otherCropSelections.length) {
            otherCropSelections[index].value = value;
            otherCropSelections[index].total = `${value} kL`;

            this.setState({
                userState: {
                    ...this.state.userState,
                    otherCropSelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeActivityName = (index: number, text: string) => {
        const { activitySelections } = this.state.userState;
        const foundDatasource = this.state.activities.find(item => item.name === text);
        const tooltip = foundDatasource && foundDatasource.tooltip;

        if (index >= 0 && index < activitySelections.length) {
            activitySelections[index] = Selection.newNumber({ name: text, type: ESourceType.Activity, value: activitySelections[index].value, tooltip });
                    
            this.setState({
                userState: {
                    ...this.state.userState,
                    activitySelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeCropName = (index: number, text: string) => {
        const { cropSelections } = this.state.userState;

        if (index >= 0 && index < cropSelections.length) {
            cropSelections[index].name = text;

            this.setState({
                userState: {
                    ...this.state.userState,
                    cropSelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeActivityValue = (index: number, value: string|number) => {
        const { activitySelections } = this.state.userState;

        if (index >= 0 && index < activitySelections.length) {
            activitySelections[index].value = value;

            this.setState({
                userState: {
                    ...this.state.userState,
                    activitySelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeCropValue = (index: number, value: string|number) => {
        const { cropSelections } = this.state.userState;

        if (index >= 0 && index < cropSelections.length) {
            cropSelections[index].value = value;

            this.setState({
                userState: {
                    ...this.state.userState,
                    cropSelections,
                }
            });

            this.updateTotals();
        }
    }

    private onChangeOtherUnlicensedName  = (index: number, value: string) => {
        const { otherUnlicensedSelections } = this.state.userState;

        if (index >= 0 && index < otherUnlicensedSelections.length) {
            otherUnlicensedSelections[index].name = value;

            this.setState({
                userState: {
                    ...this.state.userState,
                    otherUnlicensedSelections,
                }
            });

            this.updateTotals();
        }

    };

    private onChangeOtherUnlicensedValue  = (index: number, value: string|number) => {
        const { otherUnlicensedSelections } = this.state.userState;

        if (index >= 0 && index < otherUnlicensedSelections.length) {
            otherUnlicensedSelections[index].value = value;
            otherUnlicensedSelections[index].total = `${value} kL`;

            this.setState({
                userState: {
                    ...this.state.userState,
                    otherUnlicensedSelections,
                }
            });

            this.updateTotals();
        }
    };

    private onChangeStockName = (index: number, value: string) => {
        const { stockSelections } = this.state.userState;

        if (index >= 0 && index < stockSelections.length) {
            stockSelections[index].name = value;

            this.setState({
                userState: {
                    ...this.state.userState,
                    stockSelections,
                }
            });

            this.updateTotals();
        }
    };

    private onChangeStockValue = (index: number, value: string|number) => {
        const { stockSelections } = this.state.userState;

        if (index >= 0 && index < stockSelections.length) {
            stockSelections[index].value = value;

            this.setState({
                userState: {
                    ...this.state.userState,
                    stockSelections,
                }
            });

            this.updateTotals();
        }
    };

    private toggleOption(key: ESourceType, enabled: boolean) {
        const { userState } = this.state;
        let enabledOptions = userState.enabledOptions;

        if (enabledOptions) {
            if (enabled) {
                if (enabledOptions.indexOf(key) === -1) {
                    enabledOptions.push(key);
                }
            } else {
                enabledOptions = enabledOptions.filter(item => item !== key);
            }
        }

        this.setState({
            userState: {
                ...userState,
                enabledOptions,
            },
        });

        this.updateTotals();
    }

    private setProperty(key: ESourceType, value: number | string) {
        const { suburbs, userState, gardenMax } = this.state;
        const newState = {...userState};

        switch (key) {
            case ESourceType.Suburb:
                newState.suburb = Selection.newText({ name: 'Suburb', value, type: ESourceType.Suburb });
                newState.climateZone = Selection.newText({ name: 'Climate Zone', type: ESourceType.ClimateZone });

                const filteredZones = suburbs
                    .filter(item => item.name === value)
                    .map(item => item.zone)

                if (filteredZones.length === 1) {
                    newState.climateZone = Selection.newText({ name: 'Climate Zone', value: filteredZones[0], type: ESourceType.ClimateZone });
                }

                break;

            case ESourceType.ClimateZone:
                newState.climateZone.value = `${value}`;
                break;

            case ESourceType.Household: {
                newState.household.value = value;
                break;
            }

            case ESourceType.Garden: {
                newState.garden.value = value;

                if (newState.garden.value > gardenMax) {
                    return;
                }

                break;
            }

            case ESourceType.FireFightingFirePumpAndHoses: {
                newState.fireFightingFirePumpAndHoses.value = value;
                break;
            }

            case ESourceType.FireFightingSprinklerSystem: {
                newState.fireFightingSprinklerSystem.value = value;
                break;
            }
            
            case ESourceType.HorseWashdown:
                newState.horseWashdown.value = value;
                break;
            
            case ESourceType.HorseFloatWashdownSmall:
                newState.horseFloatWashdownSmall.value = value;
                break;
                
            case ESourceType.HorseFloatWashdownLarge:
                newState.horseFloatWashdownLarge.value = value;
                break;
            
            case ESourceType.WeedControl:
                newState.weedControl.value = value;
                break;

            default:
                break;
        }

        this.setState({
            userState: newState,
        });

        this.updateTotals();
    }

    private updateTotals = () => {
        setTimeout(() => {
            const {
                climateZone,
                household,
                fireFightingFirePumpAndHoses,
                fireFightingSprinklerSystem,
                garden,
                weedControl,
                stockSelections,
                cropSelections,
                activitySelections,
            } = this.state.userState;
    
            const formatNumber = (value: number) => {
                if (value === 0) {
                    return '0';
                }
    
                return numeral(value).format('0,0.00') + ' kL';
            };
    
            // Household
            household.total = formatNumber(Number(household.value) * this.state.household)
    
            // Garden
            const foundGardenMultiplier = this.state.garden.find(item => item.name === climateZone.value);
            garden.total = foundGardenMultiplier ? formatNumber(Number(garden.value) * foundGardenMultiplier.value) : (this.state.messages.page3.climateZoneNotFound || 'Climate zone not found.');
    
            // Fire Fighting Pump
            const pumpRiskMultiplier = this.state.fireFighting.find(item => item.name.indexOf('fire pump') > -1);
            fireFightingFirePumpAndHoses.total = pumpRiskMultiplier ? formatNumber(Number(fireFightingFirePumpAndHoses.value) * pumpRiskMultiplier.value) : '';
    
            // Fire Fighting Reserve
            const sprinklerRiskMultiplier = this.state.fireFighting.find(item => item.name.indexOf('sprinkler') > -1);
            fireFightingSprinklerSystem.total = sprinklerRiskMultiplier ? formatNumber(Number(fireFightingSprinklerSystem.value) * sprinklerRiskMultiplier.value) : '';

            // Weed Control
            weedControl.total = formatNumber(Number(weedControl.value) * this.state.weedControl);
    
            // Stock
            const stock = this.state.stock;
            const newStockSelections = [...stockSelections].map(item => {
                const multiplier = stock.find(stockItem => stockItem.name === item.name);
                item.total = '';
    
                if (multiplier) {
                    item.total = formatNumber(multiplier.value * Number(item.value));
                }
    
                return item;
            });
    
            // Crops
            const crops = this.state.crops;
            const newCropSelections = [...cropSelections].map(item => {
                const foundCrop = crops.find(cropItem => cropItem.name === item.name);
                item.total = this.state.messages.page4.climateZoneNotFound || 'Climate zone not found.';
    
                if (foundCrop) {
                    const foundZone = foundCrop.zones.find(zone => zone.name === climateZone.value);
                    
                    if (foundZone) {
                        item.total = formatNumber(foundZone.value * Number(item.value));
                    }
                }
    
                return item;
            });

            // Activities
            const activities = this.state.activities;
            const newAcivitySelections = [...activitySelections].map(item => {
                const multiplier = activities.find(activity => activity.name === item.name);
                item.total = '';
    
                if (multiplier) {
                    item.total = formatNumber(multiplier.value * Number(item.value));
                }
    
                return item;
            });
    
            this.setState({
                userState: {
                    ...this.state.userState,
                    activitySelections: newAcivitySelections,
                    cropSelections: newCropSelections,
                    fireFightingFirePumpAndHoses,
                    fireFightingSprinklerSystem,
                    stockSelections: newStockSelections,
                    weedControl,
                }
            });
    
            this.storeUserState();
        }, 0);
    };

    private onGotoNamedPage = (page: EPage) => {
        switch (page) {
            case EPage.UnlicensedWaterUse:
                this.onGotoPage(2);
                break;
            case EPage.LicensedWaterUse:
                this.onGotoPage(3);
                break;
            case EPage.IndustrialAndCommercial:
                this.onGotoPage(4);
                break;
            default:
                break;
        }
    };

    private onGotoPage = (newValue: number) => {
        this.setState({ page: newValue });
        localStorage.setItem('page', `${newValue}`);
    };
 
    private onNextPage = () => {
        this.onGotoPage(Math.min(this.state.page + 1, this.state.maxPages));
    };

    private onPreviousPage = () => {
        this.onGotoPage(Math.max(this.state.page - 1, 1));
    };

    private getDefaultUserState = () => ({
        activitySelections:                 [],
        climateZone:                        Selection.newText({ name: this.state.messages.page1.labelClimateZone, type: ESourceType.ClimateZone }),
        cropSelections:                     [],
        enabledOptions:                     [],
        fireFightingFirePumpAndHoses:       Selection.newNumber({ name: this.state.messages.page2.labelFireFightingPump, type: ESourceType.FireFightingFirePumpAndHoses }),
        fireFightingSprinklerSystem:        Selection.newNumber({ name: this.state.messages.page2.labelFireFightingSprinkler, type: ESourceType.FireFightingSprinklerSystem }),
        garden:                             Selection.newNumber({ name: this.state.messages.page2.labelGarden, type: ESourceType.Garden }),
        household:                          Selection.newNumber({ name: this.state.messages.page2.labelHousehold, type: ESourceType.Household }),
        otherActivitySelections:            [],
        otherCropSelections:                [],
        otherUnlicensedSelections:          [],
        stockSelections:                    [],
        suburb:                             Selection.newText({ name: this.state.messages.page1.labelSuburb, type: ESourceType.Suburb }),
        weedControl:                        Selection.newText({ name: this.state.messages.page2.labelWeedControl, type: ESourceType.WeedControl }),
    });

    private onReset = () => {
        this.setState({
            userState: {
                ...this.state.userState,
                ...this.getDefaultUserState(),
            }
        });

        localStorage.clear();
        this.updateTotals();
        this.onGotoPage(1);
    };

    private getTotal = (page: EPage) => {
        const {
            activitySelections,
            otherActivitySelections,
            cropSelections,
            otherCropSelections,
            household,
            garden,
            fireFightingFirePumpAndHoses,
            fireFightingSprinklerSystem,
            horseWashdown,
            horseFloatWashdownSmall,
            horseFloatWashdownLarge,
            weedControl,
            stockSelections,
            otherUnlicensedSelections,
            enabledOptions,
        } = this.state.userState;
        const isEnabled = (type: ESourceType) => enabledOptions.indexOf(type) > -1;

        const unlicensedTotal = () => {
            const parseTotal = (prev: number, next: string) => prev + numeral(next).value();
            return [
                ...(isEnabled(ESourceType.Household) ? [household.total] : []),
                ...(isEnabled(ESourceType.Garden) ? [garden.total] : []),
                ...(isEnabled(ESourceType.FireFightingFirePumpAndHoses) ? [fireFightingFirePumpAndHoses.total] : []),
                ...(isEnabled(ESourceType.FireFightingSprinklerSystem) ? [fireFightingSprinklerSystem.total] : []),
                ...(isEnabled(ESourceType.HorseWashdown) ? [horseWashdown.total] : []),
                ...(isEnabled(ESourceType.HorseFloatWashdownSmall) ? [horseFloatWashdownSmall.total] : []),
                ...(isEnabled(ESourceType.HorseFloatWashdownLarge) ? [horseFloatWashdownLarge.total] : []),
                ...(isEnabled(ESourceType.WeedControl) ? [weedControl.total] : []),
                ...(isEnabled(ESourceType.Stock) ? stockSelections.map(item => item.total) : []),
                ...(isEnabled(ESourceType.OtherUnlicensed) ? otherUnlicensedSelections.map(item => item.total) : []),
            ]
            .reduce(parseTotal, 0);
        }

        const licensedtotal = () => {
            const parseTotal = (prev: number, next: string) => prev + numeral(next).value();
            return [
                ...cropSelections.map(item => item.total),
                ...(isEnabled(ESourceType.OtherCrop) ? otherCropSelections.map(item => item.total) : []),
            ]
            .reduce(parseTotal, 0);
        };

        const industrialTotal = () => {
            const parseTotal = (prev: number, next: string) => prev + numeral(next).value();
            return [
                ...activitySelections.map(item => item.total),
                ...(isEnabled(ESourceType.OtherActivity) ? otherActivitySelections.map(item => item.total) : []),
            ]
            .reduce(parseTotal, 0);

        };

        switch (page) {
            case EPage.PropertyLocation: {
                break;
            }
            case EPage.UnlicensedWaterUse: {
                const total = unlicensedTotal();
                return numeral(total).format('0,0.00');
            }
            case EPage.LicensedWaterUse: {
                const total = licensedtotal();
                return numeral(total).format('0,0.00');
            }
            case EPage.IndustrialAndCommercial: {
                const total = industrialTotal();
                return numeral(total).format('0,0.00');
            }
            case EPage.TotalWaterUse: {
                const total = unlicensedTotal()  + licensedtotal() + industrialTotal();
                return numeral(total).format('0,0.00');
            }
            default:
                case EPage.PropertyLocation:
                return '';
        }

        return '';
    };
 }


// WEBPACK FOOTER //
// ./src/State/Provider.tsx